import { useState, useContext } from "react";
import { TextField, Button, Grid } from "@mui/material";
import styles from "./styles.module.css";
import { ReactComponent as UploadIcon } from "../../../images/svg/upload.svg";
import { ReactComponent as CloseRoundedIcon } from "../../../images/svg/Close.svg";
import ItemService from "../../../services/ItemService";
import toast from "react-hot-toast";
import Loader from "../../utils/Loading";
import ProductContext from "context/ProductContext";
import ShopContext from "context/ShopContext";
import { getApiErrorMessage } from "../../../utils/errorHandlingMessageUtils";

function AddCategoryPopup({ open, onClose }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kitchenName, setKitchenName] = useState("");
  const [kitchenIp, setKitchenIp] = useState(null);
  const [availableDays, setAvailableDays] = useState("");
  const [availableTimeRange, setAvailableTimeRange] = useState("");
  const [order, setOrder] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState("");

  const { addCategory } = useContext(ProductContext);
  const { selectedShop } = useContext(ShopContext);

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      padding: "0 12px",
      "& fieldset": {
        borderColor: "#CDCDCF",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#CDCDCF",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#7c7c7d",
        borderWidth: "1px",
      },
    },
    "& .MuiInputBase-input": {
      height: "40px",
      padding: 0,
      "&::placeholder": {
        color: "#7c7c7d",
        opacity: 1,
        fontSize: "14px",
      },
    },
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Enter a name for the category");
      return;
    }

    setIsLoading(true);
    try {
      const newCategory = await ItemService.addItemCategory({
        name,
        description,
        kitchen_name: kitchenName || null,
        kitchen_ip: kitchenIp || null,
        available_days: availableDays,
        available_time_range: availableTimeRange,
        order: order ? Number(order) : 1,
      });

      addCategory(newCategory);

      if (imageFile && newCategory?.id) {
        const formData = new FormData();
        formData.append("image", imageFile);
        await ItemService.uploadCategoryImage(formData, newCategory.id);
      }

      setName("");
      setDescription("");
      setKitchenName("");
      setKitchenIp(null);
      setAvailableDays("");
      setAvailableTimeRange("");
      setOrder("");
      setImageFile(null);
      toast.success("Category created successfully!");
      onClose();
    } catch (error) {
      toast.error(`Failed to create category: ${getApiErrorMessage(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 100 * 1024;

    if (file.size > maxSize) {
      setImageError("File is too large! Max size is 100 KB.");
      return;
    }

    setImageError("");
    setImageFile(file);
  };

  if (!open) return null;

  const isImageSupportValid =
    selectedShop?.image_support_validity && new Date(selectedShop.image_support_validity) >= new Date();

  return (
    <div className={styles.popupOverlay}>
      <Loader isOpen={isLoading} />

      <div className={styles.popupContainer}>
        <div className={styles.closeIcoSection}>
          <CloseRoundedIcon onClick={onClose} className={styles.icon} />
        </div>
        <div className={styles.headerSection} style={{ paddingBottom: "15px" }}>
          <div>
            <h2>Add new category</h2>
            <h4>Add and organize categories with ease</h4>
          </div>
        </div>

        <Grid container className={styles.CategoryWrapper}>
          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              placeholder="Category Name"
              value={name || ""}
              onChange={(e) => setName(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              placeholder="Kitchen Name"
              value={kitchenName || ""}
              onChange={(e) => setKitchenName(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              placeholder="Kitchen IP"
              value={kitchenIp || ""}
              onChange={(e) => setKitchenIp(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              type="number"
              placeholder="Display Order"
              value={order || ""}
              onChange={(e) => setOrder(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Description"
              value={description || ""}
              onChange={(e) => setDescription(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>
          {/* File upload section */}
          {isImageSupportValid && (
            <Grid item xs={12}>
              <div>
                {imageFile ? (
                  <div className={styles.uploadBox}>
                    <div className={styles.imagePreview}>
                      <img src={URL.createObjectURL(imageFile)} alt="Preview" />
                      <CloseRoundedIcon className={styles.removeIcon} onClick={() => setImageFile(null)} />
                    </div>
                    <div className={styles.uploadDetails}>
                      <Button className={styles.uploadButton} component="label">
                        <UploadIcon />
                        Upload New Image
                        <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                      </Button>
                      <h3>Choose image or Drop it here</h3>
                      <h4>JPG, JPEG, and PNG (Max size:100 KB)</h4>
                      {imageError && (
                        <h2 style={{ color: "red", marginTop: "10px", fontSize: "12px" }}>{imageError}</h2>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={styles.uploadBox} style={{ flexDirection: "column", padding: "23px 0" }}>
                    <Button className={styles.uploadButton} component="label">
                      <UploadIcon />
                      Upload
                      <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                    </Button>
                    <h3>Choose image or Drop it here</h3>
                    <h4>JPG, JPEG, and PNG (Max size:100 KB)</h4>
                    {imageError && <h2 style={{ color: "red", marginTop: "10px", fontSize: "12px" }}>{imageError}</h2>}
                  </div>
                )}
              </div>
            </Grid>
          )}
        </Grid>

        <div className={styles.actionButtons}>
          <Button onClick={onClose} className={styles.buttonGray}>
            Cancel
          </Button>
          <Button onClick={handleCreate} className={styles.buttonRed}>
            Create
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AddCategoryPopup;
